import React, { createContext, useContext, useMemo } from 'react';
import { useFetch, CPLAT_API_ROUTES } from '@/api';
import { useAppSelector } from '@/redux/store';
import type { Monitoring, MonitoringGoodsDetail } from '@/types/api';
// 1. Context 생성
type MonitoringGoodsResponseItem = Monitoring & {
  EnabledMonitoringCount?: number;
};

type MonitoringGoodsContextType = {
  goods: MonitoringGoodsDetail[];
  enabledMonitoringCount: number | null;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
};

const MonitoringGoodsContext = createContext<MonitoringGoodsContextType | undefined>(undefined);

// 2. Provider 구현
export const MonitoringGoodsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const user = useAppSelector((state) => state.user);

  // react-query로 데이터 패칭
  // API 호출
  const { data, isLoading, isError, refetch } = useFetch<{ data: MonitoringGoodsResponseItem[] }>(
    CPLAT_API_ROUTES.getUserMonitoringGoods,
    {
      method: 'POST',
      params: {
        cplatToken: user.CplatToken,
      },
    },
  );

  const monitoringInfo = useMemo(() => data?.data[0], [data]);

  // 상품 리스트/허용 개수 추출
  const goods = useMemo(() => monitoringInfo?.MonitoringGoodsDetailList ?? [], [monitoringInfo]);
  const enabledMonitoringCount = useMemo(
    () => monitoringInfo?.EnabledMonitoringCount ?? null,
    [monitoringInfo],
  );

  // 컨텍스트 값
  const value = useMemo(
    () => ({ goods, enabledMonitoringCount, isLoading, isError, refetch }),
    [goods, enabledMonitoringCount, isLoading, isError, refetch],
  );

  return (
    <MonitoringGoodsContext.Provider value={value}>{children}</MonitoringGoodsContext.Provider>
  );
};

// 3. 커스텀 훅
export const useMonitoringGoods = () => {
  const context = useContext(MonitoringGoodsContext);
  if (!context) throw new Error('useMonitoringGoods must be used within MonitoringGoodsProvider');
  return context;
};
